import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
  FaInbox,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaLink,
  FaBuilding,
  FaUser,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';

const STATUS_META = {
  pending: { label: 'Afventer godkendelse', classes: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'Godkendt', classes: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  active: { label: 'Aktiv', classes: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  rejected: { label: 'Afvist', classes: 'bg-red-100 text-red-800 border-red-300' },
  expired: { label: 'Udløbet', classes: 'bg-gray-100 text-gray-700 border-gray-300' },
};

const AdminAdRequestsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchRequests();
  }, [authLoading, isAdmin, statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${API}/admin/ad-requests`, { params, withCredentials: true });
      setRequests(res.data || []);
    } catch (err) {
      console.error('Error fetching ad requests:', err);
      toast.error('Kunne ikke hente annonceforespørgsler');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (req, status, extra = {}) => {
    if (status === 'approved') {
      // Fetch slot availability so admin can pick which slot
      let availability = null;
      try {
        const res = await axios.get(`${API}/admin/banner-slot-availability`, {
          params: { country: 'DK' },
          withCredentials: true,
        });
        availability = res.data;
      } catch (err) {
        console.error('Could not load slot availability:', err);
        toast.error('Kunne ikke hente slot-status');
        return;
      }

      const freeSlots = (availability?.slots || []).filter((s) => !s.occupied);
      if (freeSlots.length === 0) {
        toast.error('Alle 9 slots er optaget. Frigør en plads først (sæt udløbet/inaktiv).');
        return;
      }
      const freeList = freeSlots.map((s) => s.slot_number).join(', ');
      const pick = window.prompt(
        `Vælg slot (1-9) til "${req.company}".\n\nLedige slots: ${freeList}`,
        String(freeSlots[0].slot_number)
      );
      if (pick === null) return; // cancelled
      const slot_number = parseInt(pick, 10);
      if (!Number.isInteger(slot_number) || slot_number < 1 || slot_number > 9) {
        toast.error('Slot skal være et tal mellem 1 og 9');
        return;
      }
      if (!freeSlots.some((s) => s.slot_number === slot_number)) {
        toast.error(`Slot ${slot_number} er allerede optaget`);
        return;
      }
      extra = { ...extra, slot_number, placement: 'bottom_banner', country: 'DK' };

      const ok = window.confirm(
        `Godkend annonce fra "${req.company}" og placér i slot ${slot_number}? Banneret bliver aktivt med det samme.`
      );
      if (!ok) return;
    } else if (status === 'rejected') {
      const ok = window.confirm(`Afvis annonceforespørgsel fra "${req.company}"?`);
      if (!ok) return;
    }
    try {
      setBusyId(req.id);
      await axios.put(
        `${API}/admin/ad-requests/${req.id}/status`,
        { status, ...extra },
        { withCredentials: true }
      );
      toast.success(`Status sat til: ${STATUS_META[status]?.label || status}`);
      fetchRequests();
    } catch (err) {
      console.error('Status update error:', err);
      toast.error(err?.response?.data?.detail || 'Kunne ikke opdatere status');
    } finally {
      setBusyId(null);
    }
  };

  const deleteRequest = async (req) => {
    if (!window.confirm(`Slet forespørgsel fra "${req.company}"? Dette kan ikke fortrydes.`)) return;
    try {
      setBusyId(req.id);
      await axios.delete(`${API}/admin/ad-requests/${req.id}`, { withCredentials: true });
      toast.success('Forespørgsel slettet');
      fetchRequests();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Kunne ikke slette');
    } finally {
      setBusyId(null);
    }
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const counts = requests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-ad-requests-page">
      <button
        onClick={() => navigate('/admin/ads')}
        className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-4 text-sm"
      >
        <FaArrowLeft />
        Tilbage til reklamer
      </button>

      <div className="flex items-center gap-3 mb-6">
        <FaInbox className="text-cyan-500 text-3xl" />
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Annonceforespørgsler</h1>
          <p className="text-gray-600 text-sm">Forespørgsler indsendt via /advertise</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {['pending', 'active', 'approved', 'rejected', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            className={`text-left p-3 rounded-xl border-2 transition-all ${
              statusFilter === s ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            data-testid={`filter-${s}`}
          >
            <div className="text-xs text-gray-600">{STATUS_META[s].label}</div>
            <div className="text-2xl font-bold text-gray-800">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
            statusFilter === 'all'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
          }`}
        >
          Alle ({requests.length})
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <FaInbox className="mx-auto text-6xl text-gray-300 mb-4" />
          <p className="text-gray-500">Ingen annonceforespørgsler {statusFilter !== 'all' ? `med status "${STATUS_META[statusFilter]?.label}"` : ''}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const meta = STATUS_META[req.status] || STATUS_META.pending;
            const isOpen = expanded[req.id];
            return (
              <div
                key={req.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                data-testid={`ad-request-${req.id}`}
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-start gap-4"
                  onClick={() => toggleExpand(req.id)}
                >
                  <img
                    src={req.image_url}
                    alt={req.company}
                    className="w-24 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-bold text-gray-800">{req.company}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.classes}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                      <span><FaUser className="inline mr-1" />{req.contact_person}</span>
                      <span><FaEnvelope className="inline mr-1" />{req.email}</span>
                      <span><FaCalendarAlt className="inline mr-1" />{req.period_months} mdr.</span>
                      <span className="text-gray-400">
                        {new Date(req.created_at).toLocaleDateString('da-DK', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                  {isOpen ? <FaChevronUp className="text-gray-400 mt-2" /> : <FaChevronDown className="text-gray-400 mt-2" />}
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <DetailRow icon={<FaBuilding />} label="Firma" value={req.company} />
                      <DetailRow icon={<FaUser />} label="Kontaktperson" value={req.contact_person} />
                      <DetailRow icon={<FaEnvelope />} label="E-mail" value={<a href={`mailto:${req.email}`} className="text-cyan-600 hover:underline">{req.email}</a>} />
                      <DetailRow icon={<FaPhone />} label="Telefon" value={req.phone || '-'} />
                      <DetailRow icon={<FaCalendarAlt />} label="Periode" value={`${req.period_months} måneder`} />
                      <DetailRow icon={<FaLink />} label="Klik-link" value={<a href={req.click_link} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline break-all">{req.click_link}</a>} />
                    </div>

                    {req.comment && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Kommentar</div>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{req.comment}</div>
                      </div>
                    )}

                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-600 mb-1">Banner-preview</div>
                      <img
                        src={req.image_url}
                        alt="Banner"
                        className="max-w-full md:max-w-md rounded-lg border border-gray-200"
                      />
                    </div>

                    {req.admin_note && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Admin-note</div>
                        <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-900">{req.admin_note}</div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                      {req.status === 'pending' && (
                        <>
                          <button
                            disabled={busyId === req.id}
                            onClick={() => updateStatus(req, 'approved')}
                            data-testid={`approve-${req.id}`}
                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <FaCheckCircle />
                            Godkend & opret banner
                          </button>
                          <button
                            disabled={busyId === req.id}
                            onClick={() => updateStatus(req, 'rejected')}
                            data-testid={`reject-${req.id}`}
                            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <FaTimesCircle />
                            Afvis
                          </button>
                        </>
                      )}
                      {req.status === 'active' && (
                        <button
                          disabled={busyId === req.id}
                          onClick={() => updateStatus(req, 'expired')}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                        >
                          Marker som udløbet
                        </button>
                      )}
                      {req.status !== 'pending' && req.status !== 'active' && (
                        <button
                          disabled={busyId === req.id}
                          onClick={() => updateStatus(req, 'pending')}
                          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                        >
                          Sæt tilbage til afventer
                        </button>
                      )}
                      <button
                        disabled={busyId === req.id}
                        onClick={() => deleteRequest(req)}
                        className="ml-auto px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        <FaTrash />
                        Slet
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ icon, label, value }) => (
  <div>
    <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
      <span className="text-gray-400">{icon}</span>{label}
    </div>
    <div className="text-sm text-gray-800">{value}</div>
  </div>
);

export default AdminAdRequestsPage;
